/**
 * SurveyEditorPage — survey builder + survey questions library.
 * Designed from the survey-editors briefing (no legacy canonical exists).
 * Tabs: Surveys · Survey Questions
 */
import { useState, useEffect, type CSSProperties } from "react";
import { MediaRow, alpha, Btn, Card, Dropdown, Icon, IconBtn, Input, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, EmptyState, ExplainerFooter, RowActions, StatTile } from "../../shell/composites";
import { useViewMode, ViewToggle, CARD_GRID, CardActionBar } from "../../shell/list-composites";
import { can, onTenantChange, onTenantSwitching } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { listSurveys, createSurvey, updateSurvey, deleteSurvey, duplicateSurvey, listSurveyChannels } from "@v0y4ge/api-client";
import { useSurveyQuestions } from "./survey-questions-store";
import { QTYPE, type Question } from "../questions/useQuestions";
import { logUpsert } from "../../lib/audit";
import { useT, t as i18nT } from "../../lib/i18n";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";

/* ── Types ───────────────────────────────────────────────────────────── */
const SQ_CHANNELS = [ // ui-config — fallback ONLY until listSurveyChannels resolves (addendum item 3 residue); ids/colors below are presentation defaults for known channel ids
  { id: "email", name: "Email", color: "#0E7490" }, { id: "sms", name: "SMS", color: "#1D7A3A" },
  { id: "whatsapp", name: "WhatsApp", color: "#25D366" }, { id: "tiktok", name: "TikTok", color: "#000" },
  { id: "instagram", name: "Instagram", color: "#E1306C" }, { id: "facebook", name: "Facebook", color: "#1877F2" },
];

interface SurveySend {
  mode: "relative"|"absolute";
  trigger?: "before"|"after"; amount?: number; unit?: "Minutes"|"Hours"|"Days"|"Weeks";
  datetime?: string; channel: string;
}
interface SurveyItem { qid: string; required?: boolean; }
interface Survey {
  id: string; name: string; description: string; status: "approved"|"draft";
  usedBy: number; send: SurveySend; items: SurveyItem[]; updatedAt: string;
}

/* ── Shared surveys store ─────────────────────────────────────────────────
 * Module-level so a survey created/deployed in the editor appears on the list
 * immediately. One fetch hydrates it; upserts call the real create/update ops
 * (awaited, UIR-09 fix 2026-07-25) and reconcile the response into the cache. */
function surveyFromDto(s: any): Survey {
  return { id: s.id, name: s.name ?? "", description: s.description ?? "", status: s.status === "approved" ? "approved" : "draft", usedBy: s.usedBy ?? 0, send: s.send ?? { mode: "relative", trigger: "after", amount: 24, unit: "Hours", channel: "email" }, items: (s.items ?? []).map((it: any) => ({ qid: it.qid ?? it.questionId ?? it.id, required: it.required })), updatedAt: s.updatedAt ?? s.updated_at ?? "—" };
}
let _svCache: Survey[] | null = null;
let _svLoadStarted = false;
const _svSubs = new Set<() => void>();
const _svEmit = () => _svSubs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
onTenantSwitching(() => { _svCache = null; _svLoadStarted = false; _svEmit(); });
onTenantChange(() => { if (can("surveys.read")) void _svLoad(); }); // admin/surveys + channels — gated (docket 2026-09-17)
// Addendum item 3 residue: the channel picker sources from listSurveyChannels
// (kit-served); SQ_CHANNELS is only the presentation fallback until it lands.
let _chCache: { id: string; name: string; color: string }[] | null = null;
function svChannels() { return _chCache ?? SQ_CHANNELS; }
async function _chLoad(): Promise<void> {
  try {
    const r: any = typeof listSurveyChannels === "function" ? await listSurveyChannels() : null;
    const arr = (r?.channels ?? r?.data ?? []) as any[];
    if (Array.isArray(arr) && arr.length) {
      _chCache = arr.map((c: any) => { const id = String(c.id ?? c); const known = svChannels().find((k) => k.id === id); return { id, name: String(c.name ?? known?.name ?? id), color: String(c.color ?? known?.color ?? T.p1) }; });
      _svEmit();
    }
  } catch (e) { console.warn("listSurveyChannels failed, using fallback:", e); }
}
// Seed survey wiring the post-event experience questions (offline fallback).
async function _svLoad(): Promise<void> {
  if (_svLoadStarted) return;
  _svLoadStarted = true;
  void _chLoad();
  try {
    const r: any = await listSurveys({} as any);
    const fetched = ((r?.surveys ?? r?.data ?? []) as any[]).map(surveyFromDto);
    const local = (_svCache ?? []).filter((s) => !fetched.some((f) => f.id === s.id));
    _svCache = fetched.length ? [...local, ...fetched] : (_svCache ?? []);
    _svEmit();
  } catch { _svCache = _svCache ?? []; _svEmit(); }
}
async function upsertSurvey(s: Survey): Promise<Survey> {
  const cur = _svCache ?? [];
  const isNew = !cur.some((x) => x.id === s.id);
  const payload = { name: s.name, description: s.description, status: s.status, send: s.send, items: s.items };
  let saved: Survey = { ...s, updatedAt: "Just now" };
  try {
    if (isNew) {
      const res: any = await createSurvey(payload as any);
      const created = res?.survey ?? res;
      if (created) saved = surveyFromDto({ ...created, ...payload });
    } else {
      const res: any = await updateSurvey(s.id, payload as any);
      const updated = res?.survey ?? res;
      if (updated) saved = surveyFromDto({ ...updated, ...payload });
    }
  } catch (e) { console.warn(isNew ? "createSurvey failed:" : "updateSurvey failed:", e); throw e; }
  logUpsert("survey", isNew ? null : (cur.find((x) => x.id === s.id) as unknown as Record<string, unknown>), saved as unknown as Record<string, unknown>, { name: saved.name });
  _svCache = isNew ? [saved, ...cur] : cur.map((x) => (x.id === s.id ? saved : x));
  _svEmit();
  return saved;
}
export function useSurveys() {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _svSubs.add(cb);
    void _svLoad();
    return () => { _svSubs.delete(cb); };
  }, []);
  return { surveys: _svCache ?? [], loading: _svCache === null };
}

/* ── Seed data ───────────────────────────────────────────────────────── */

let _sqSeq = 100;
const _sqId = (p: string) => `${p}_${(++_sqSeq).toString(36)}${Date.now().toString(36).slice(-3)}`;

/* ── Shared helpers ──────────────────────────────────────────────────── */
function ChipTag({ label, color }: { label: string; color: string }) {
  return <span style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: color, color: "#fff" }}>{label}</span>;
}
function sendLabel(s: SurveySend): string {
  const ch = svChannels().find((c) => c.id === s.channel)?.name ?? s.channel;
  if (s.mode === "absolute") return `Absolute · ${s.datetime ?? "—"} · ${ch}`;
  return `${s.amount} ${s.unit} ${s.trigger} event · ${ch}`;
}

const thS: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 11, textTransform: "uppercase", color: T.soft };
const tdS: CSSProperties = { padding: "12px 16px", verticalAlign: "middle" };

function surveyTabs(nav: ReturnType<typeof useNav>, active: string, counts: { surveys?: number; questions?: number } = {}) {
  return [
    { id: "questions", label: i18nT("surveys.editorPage.surveyQuestions", "Survey questions"), count: counts.questions, onClick: () => nav.go("surveys.questions") },
    { id: "surveys",   label: i18nT("surveys.editorPage.surveyFlows", "Survey flows"),     count: counts.surveys,   onClick: () => nav.go("surveys") },
  ];
}

/* ═══ Survey Questions tab ═══════════════════════════════════════════ */
function SurveyQuestionsTab() {
  const t = useT();
  const nav = useNav();
  const store = useSurveyQuestions();
  const sqList = store.questions();
  const [query, setQuery] = useState("");
  const canWrite = can("questions.write");
  const filtered = sqList.filter((q) => !query.trim() || q.label.toLowerCase().includes(query.toLowerCase()));
  const dup = async (q: Question) => {
    try {
      await store.duplicateQ(q.id);
      toast({ tone: "success", title: t("surveys.editorPage.questionDuplicated", "Question duplicated") });
    } catch (e) {
      console.warn("duplicateSurveyQuestion failed:", e);
      toast({ tone: "danger", title: t("surveys.editorPage.couldnTDuplicateThatQuestion", "Couldn't duplicate that question"), subtitle: t("surveys.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = async (q: Question) => {
    try {
      await store.removeQ(q.id);
      toast({ tone: "warn", title: t("surveys.editorPage.questionDeleted", "Question deleted") });
    } catch (e) {
      console.warn("deleteSurveyQuestion failed:", e);
      toast({ tone: "danger", title: t("surveys.editorPage.couldnTDeleteThatQuestion", "Couldn't delete that question"), subtitle: t("surveys.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  return (
    <Screen rail="surveys" tabs={surveyTabs(nav, "questions", { questions: sqList.length })} activeTab="questions">
      <PageHeader title={t("surveys.editorPage.surveyQuestions", "Survey questions")} subtitle={t("surveys.editorPage.reusableQuestionsForPostEventSurveysBuiltWithTheSameEditorAs", "Reusable questions for post-event surveys. Built with the same editor as booking questions.")}
        actions={canWrite ? <Btn icon={<Icon.plus/>} onClick={() => nav.go("surveys.question.edit", { new: 1 })}>{t("surveys.editorPage.newQuestion", "New question")}</Btn> : null}/>
      <div style={{ flex:1, overflow:"auto", padding:"16px 24px 28px", display:"flex", flexDirection:"column" }}>
        <div style={{ margin: "0 -24px" }}><FilterBar>
          <Input placeholder={t("surveys.editorPage.findASurveyQuestion", "Find a survey question…")} value={query} onChange={(e) => setQuery(e.target.value)} style={{ minWidth: 260 }}/>
          <span style={{ flex:1 }}/><span style={{ fontSize:12, color:T.soft }}>{filtered.length} {t("surveys.editorPage.of", "of")}{" "}{sqList.length}</span>
        </FilterBar></div>
        {filtered.length === 0
          ? <EmptyState icon={<Icon.help size={26}/>} title={t("surveys.editorPage.noSurveyQuestionsYet", "No survey questions yet")} body={t("surveys.editorPage.createYourFirstSurveyQuestionSameEditorAsBookingQuestions", "Create your first survey question — same editor as booking questions.")} action={canWrite ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("surveys.question.edit", { new: 1 })}>{t("surveys.editorPage.newQuestion", "New question")}</Btn> : null}/>
          : <Card style={{ padding:0, marginTop:14 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:T.font, fontSize:13 }}>
                <thead><tr style={{ background:T.n6 }}>
                  <th style={thS}>{t("surveys.editorPage.question", "Question")}</th><th style={thS}>{t("surveys.editorPage.type", "Type")}</th><th style={thS}>{t("surveys.editorPage.updated", "Updated")}</th><th style={{ ...thS, width:140 }}>{t("surveys.editorPage.actions", "Actions")}</th>
                </tr></thead>
                <tbody>{filtered.map((q, i) => (
                  <tr key={q.id} onClick={() => nav.go(canWrite ? "surveys.question.edit" : "surveys.question.edit", canWrite ? { id: q.id } : { id: q.id, view: 1 })} style={{ borderTop:i?`1px solid ${T.border}`:"none", cursor:"pointer" }}>
                    <td style={tdS}><div style={{ fontWeight:600, color:T.n1 }}>{q.label}</div>{q.help ? <div style={{ fontSize:11.5, color:T.soft }}>{q.help}</div> : null}</td>
                    <td style={tdS}><span style={{ fontSize:12, fontWeight:500, color:T.n2, padding:"3px 8px", borderRadius:999, background:T.n6, border:`1px solid ${T.border}` }}>{QTYPE(q.type).label}</span></td>
                    <td style={{ ...tdS, color:T.soft }}>{q.updated}</td>
                    <td style={tdS} onClick={(e)=>e.stopPropagation()}><RowActions onEdit={canWrite?()=>nav.go("surveys.question.edit",{ id:q.id }):undefined} onDuplicate={canWrite?()=>dup(q):undefined} moreItems={canWrite?[{ icon:<Icon.trash size={14}/>, label:t("common.actions.delete", "Delete"), danger:true as const, onClick:()=>del(q) }]:[]}/></td>
                  </tr>
                ))}</tbody>
              </table>
            </Card>}
        <ExplainerFooter><b>{t("surveys.editorPage.sameEditorAsBookingQuestions", "Same editor as booking questions.")}</b> {t("surveys.editorPage.buildAQuestionHereThenAddItToAnySurveyAndDeploy", "Build a question here, then add it to any survey and deploy.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}

/* ═══ Surveys list tab ═══════════════════════════════════════════════ */
function SurveysListTab() {
  const t = useT();
  const nav = useNav();
  const { surveys: surveyList } = useSurveys();
  const [, rerender] = useState(0);
  const [query, setQuery] = useState("");
  const [view, setView] = useViewMode("surveys:view");
  const canWrite = can("questions.write");
  /* Folder Library shell (family "surveys") — mounted, not copied. */
  const lib = useFolderLibrary({
    family: "surveys",
    ids: surveyList.map((x) => x.id),
    canWrite,
    allLabel: t("surveys.editorPage.allSurveys", "All surveys"),
    searchAllLabel: t("surveys.editorPage.searchAllSurveys", "Search all surveys"),
    query,
    failTitle: t("surveys.editorPage.couldnTUpdateSurvey", "Couldn't update survey"),
    onAfterPurge: () => { _svCache = null; _svLoadStarted = false; void _svLoad(); },
  });
  const poolSet = new Set(lib.pool);
  const filtered = surveyList.filter((s) => poolSet.has(s.id) && (!query.trim() || s.name.toLowerCase().includes(query.toLowerCase())));
  const dup = async (s: Survey) => {
    try {
      const res: any = await duplicateSurvey(s.id);
      const created = res?.survey ?? res;
      const copy = created ? surveyFromDto(created) : { ...s, id: _sqId("sv"), name: i18nT("surveys.editorPage.sNameCopy.icu", "{value0} (copy)", { value0: s.name }), status: "draft" as const, usedBy: 0 };
      _svCache = [copy, ...(_svCache ?? [])]; _svEmit(); rerender((v) => v + 1);
      toast({ tone: "success", title: t("surveys.editorPage.surveyDuplicated", "Survey duplicated") });
    } catch (e) {
      console.warn("duplicateSurvey failed:", e);
      toast({ tone: "danger", title: t("surveys.editorPage.couldnTDuplicateSurvey", "Couldn't duplicate survey"), subtitle: t("surveys.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const del = async (s: Survey) => {
    if (s.usedBy > 0) { toast({ tone: "warn", title: t("surveys.editorPage.inUse", "In use") }); return; }
    try {
      await deleteSurvey(s.id);
      _svCache = (_svCache ?? []).filter((x) => x.id !== s.id); _svEmit(); rerender((v) => v + 1);
      toast({ tone: "warn", title: t("surveys.editorPage.surveyDeleted", "Survey deleted") });
    } catch (e) {
      console.warn("deleteSurvey failed:", e);
      toast({ tone: "danger", title: t("surveys.editorPage.couldnTDeleteSurvey", "Couldn't delete survey"), subtitle: t("surveys.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  const mkActions = (s: Survey) => ({
    onView: ()=>nav.go("surveys.edit",{id:s.id,view:1}), viewLabel:"Preview",
    onEdit: canWrite?()=>nav.go("surveys.edit",{id:s.id}):undefined,
    onDuplicate: canWrite?()=>dup(s):undefined,
    moreItems: canWrite?[lib.foldersMenuItem(s.id),{icon:<Icon.trash size={14}/>,label:t("surveys.editorPage.deleteSurvey", "Delete survey"),danger:true as const,onClick:()=>del(s)}]:[],
  });

  return (
    <Screen rail="surveys" tabs={surveyTabs(nav,"surveys", { surveys: (surveyList ?? []).length })} activeTab="surveys">
      <PageHeader title={t("surveys.editorPage.surveys", "Surveys")} subtitle={t("surveys.editorPage.postEventFeedbackFormsSentAutomaticallyAtAConfiguredTime", "Post-event feedback forms sent automatically at a configured time.")}
        actions={<>
          <ViewToggle mode={view} onChange={setView}/>
          {canWrite?<Btn icon={<Icon.plus/>} onClick={()=>nav.go("surveys.edit",{new:1})}>{t("surveys.editorPage.newSurvey", "New survey")}</Btn>:null}
        </>}/>
      <FolderShellFrame lib={lib}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:16 }}>
          <StatTile label={t("surveys.editorPage.surveysLive", "Surveys live")}    value={String(surveyList.filter((s)=>s.status==="approved").length)} icon={<Icon.check size={14}/>} accent={T.green1}/>
          {/* §3e (2026-07-19): "Total sent · 30d" + "Avg response rate" removed —
              invented metrics must not render beside a real count. Restore when
              a survey-analytics op exists (declared). */}
        </div>
        <div style={{ padding: "10px 0 4px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Input full={false} leading={<Icon.search size={14} />} placeholder={t("surveys.editorPage.findASurvey", "Find a survey…")} value={query} onChange={(e)=>setQuery(e.target.value)} style={{ minWidth:240 }}/>
          <span style={{ flex:1 }}/><span style={{ fontSize:12, color:T.soft }}>{filtered.length} {t("surveys.editorPage.of", "of")}{" "}{lib.pool.length}</span>
        </div>
        <FolderShellBars lib={lib}/>
        {filtered.length===0
          ? <EmptyState icon={<Icon.check size={26}/>} title={t("surveys.editorPage.noSurveysMatch", "No surveys match")} body={t("surveys.editorPage.createOneToCollectPostEventFeedback", "Create one to collect post-event feedback.")} action={canWrite?<Btn icon={<Icon.plus size={14}/>} onClick={()=>nav.go("surveys.edit",{new:1})}>{t("surveys.editorPage.newSurvey", "New survey")}</Btn>:null}/>
          : view==="grid"?(
            <div style={{ display:"grid", gridTemplateColumns:CARD_GRID, gap:16, marginTop:14 }}>
              {filtered.map((s)=>{
                const ch=svChannels().find((c)=>c.id===s.send.channel);
                return (
                  <Card key={s.id} {...lib.dragProps(s.id)} style={{ padding:0, display:"flex", flexDirection:"column", cursor:"pointer", overflow:"hidden", outline: lib.sel.has(s.id) ? `2px solid ${T.p1}` : "none" }} onClick={()=>nav.go("surveys.edit",{id:s.id})}>
                    <div style={{ padding:"16px 18px 14px", flex:1, display:"flex", flexDirection:"column", gap:10 }}>
                      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
                        {canWrite ? <input type="checkbox" checked={lib.sel.has(s.id)} onClick={(e)=>e.stopPropagation()} onChange={(e)=>lib.toggleSel(s.id, e.target.checked)} style={{ accentColor: T.p1, marginTop: 8 }}/> : null}
                        <span style={{ width:32, height:32, borderRadius:7, background:T.p4, color:T.p1, display:"inline-flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon.check size={16}/></span>
                        <div><div style={{ fontSize:14.5, fontWeight:700, color:T.n1 }}>{s.name}</div>{s.description?<div style={{ fontSize:12, color:T.soft, marginTop:3 }}>{s.description}</div>:null}</div>
                      </div>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        <Pill>{s.items.length} {t("surveys.editorPage.questions17d5efa6", "questions")}</Pill>
                        {ch?<ChipTag label={ch.name} color={ch.color}/>:null}
                      </div>
                      <div style={{ fontSize:12, color:T.soft, display:"flex", alignItems:"center" }}>{sendLabel(s.send)}{lib.folderChips(s.id)}</div>
                    </div>
                    <CardActionBar {...mkActions(s)}/>
                  </Card>
                );
              })}
            </div>
          ):(
            <Card style={{ padding:0, marginTop:14 }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:T.font, fontSize:13 }}>
                <thead><tr style={{ background:T.n6 }}>
                  {canWrite ? <th style={{ ...thS, width:34, paddingRight:0 }}><input type="checkbox" aria-label={t("surveys.editorPage.ariaLabel", "Select all")} checked={filtered.length>0 && filtered.every((r)=>lib.sel.has(r.id))} onChange={(e)=>lib.setSel(e.target.checked ? new Set(filtered.map((r)=>r.id)) : new Set())} style={{ accentColor:T.p1 }}/></th> : null}
                  <th style={thS}>{t("surveys.editorPage.survey", "Survey")}</th><th style={thS}>{t("surveys.editorPage.questions", "Questions")}</th><th style={thS}>{t("surveys.editorPage.sendTiming", "Send timing")}</th><th style={thS}>{t("surveys.editorPage.channel", "Channel")}</th><th style={{ ...thS, width:120 }}>{t("surveys.editorPage.actions", "Actions")}</th></tr></thead>
                <tbody>{filtered.map((s,i)=>{
                  const ch=svChannels().find((c)=>c.id===s.send.channel);
                  return (
                    <tr key={s.id} {...lib.dragProps(s.id)} onClick={()=>nav.go("surveys.edit",{id:s.id})} style={{ borderTop:i?`1px solid ${T.border}`:"none", cursor:"pointer", background: lib.sel.has(s.id) ? T.p4 : "transparent" }}>
                      {canWrite ? <td style={{ ...tdS, width:34, paddingRight:0 }} onClick={(e)=>e.stopPropagation()}><input type="checkbox" checked={lib.sel.has(s.id)} onChange={(e)=>lib.toggleSel(s.id, e.target.checked)} style={{ accentColor:T.p1 }}/></td> : null}
                      <td style={tdS}><div style={{ fontWeight:600, color:T.n1, display:"flex", alignItems:"center" }}>{s.name}{lib.folderChips(s.id)}</div>{s.description?<div style={{ fontSize:11.5, color:T.soft }}>{s.description}</div>:null}</td>
                      <td style={{ ...tdS, color:T.n1 }}>{s.items.length}</td>
                      <td style={{ ...tdS, color:T.soft, fontSize:12 }}>{sendLabel(s.send)}</td>
                      <td style={tdS}>{ch?<ChipTag label={ch.name} color={ch.color}/>:null}</td>
                      <td style={tdS} onClick={(e)=>e.stopPropagation()}><RowActions {...mkActions(s)}/></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </Card>
          )}
        <ExplainerFooter><b>{t("surveys.editorPage.surveys", "Surveys")}</b> {t("surveys.editorPage.collectStructuredPostEventFeedbackConfigureSendTimingChannelAndQuestionO", "collect structured post-event feedback. Configure send timing, channel, and question order. Delivery reuses your existing messaging infrastructure.")}</ExplainerFooter>
      </FolderShellFrame>
      <FolderShellOverlays lib={lib}/>
    </Screen>
  );
}

/* ═══ Survey editor ══════════════════════════════════════════════════ */
export function SurveyEditorPage() {
  const t = useT();
  const sqStore = useSurveyQuestions();
  const editorSqList = sqStore.questions();
  const { surveys: allSurveys } = useSurveys();
  const nav = useNav();
  const isNew = !!(nav.params?.new);
  const id = nav.params?.id as string|undefined;
  const viewOnly = !!(nav.params?.view);
  const canWrite = !viewOnly && can("questions.write");
  const [, rerender2] = useState(0);
  const [form, setForm] = useState<Survey>(() => ({ id: _sqId("sv"), name: "", description: "", status: "draft", usedBy: 0, send: { mode: "relative", trigger: "after", amount: 24, unit: "Hours", channel: "email" }, items: [], updatedAt: "Just now" }));
  useEffect(() => {
    if (!isNew && id) {
      const found = allSurveys.find((s) => s.id === id);
      if (found) setForm(found);
    }
  }, [id, isNew, allSurveys]);
  const [picking, setPicking] = useState(false);
  const set = (p: Partial<Survey>) => setForm((f)=>({...f,...p}));
  const setSend = (p: Partial<SurveySend>) => setForm((f)=>({...f,send:{...f.send,...p}}));
  const addItem = (q: Question) => { if (form.items.some((it)=>it.qid===q.id)) return; setForm((f)=>({...f,items:[...f.items,{qid:q.id}]})); };
  const removeItem = (qid: string) => setForm((f)=>({...f,items:f.items.filter((it)=>it.qid!==qid)}));
  const move = (i: number, dir: number) => setForm((f)=>{ const j=i+dir; if(j<0||j>=f.items.length) return f; const n=f.items.slice(); [n[i],n[j]]=[n[j],n[i]]; return {...f,items:n}; });
  const save = async () => {
    try {
      await upsertSurvey(form);
      toast({ tone:"success", title:isNew?"Survey created":"Survey saved", subtitle:t("surveys.editorPage.availableUnderSurveys", "Available under Surveys.") });
      nav.go("surveys");
    } catch (e) {
      toast({ tone: "danger", title: t("surveys.editorPage.couldnTSaveSurvey", "Couldn't save survey"), subtitle: t("surveys.editorPage.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    }
  };
  return (
    <Screen rail="surveys">
      <PageHeader
        title={isNew?t("surveys.editorPage.newSurvey", "New survey"):(viewOnly?form.name||t("builder.eventFlowBuilder.survey", "Survey"):t("surveys.surveyEditorPage.editFormNameSurvey", "Edit · {value0}", { value0: form.name||t("builder.eventFlowBuilder.survey", "Survey") }))}
        subtitle={t("surveys.editorPage.formItemsLengthQuestionFormItemsLength1SSendlabelFormSend.icu", "{count, plural, one {# question} other {# questions}} · {sendLabel}", { count: form.items.length, sendLabel: sendLabel(form.send) })}
        breadcrumb={<span style={{ cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4 }} onClick={()=>nav.go("surveys")}><Icon.arrow dir="left" size={11}/> {t("surveys.editorPage.surveys", "Surveys")}</span>}
        actions={viewOnly?<Btn icon={<Icon.edit size={14}/>} onClick={()=>nav.go("surveys.edit",{id:form.id})}>{t("common.actions.edit", "Edit")}</Btn>:<>
          <Btn variant="secondary" onClick={()=>nav.go("surveys")}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.check size={14}/>} disabled={!form.name.trim()||form.items.length===0} onClick={save}>{isNew?t("surveys.surveyEditorPage.create", "Create"):t("common.actions.save", "Save")}</Btn>
        </>}
      />
      <div style={{ flex:1, overflow:"auto", padding:"16px 24px 32px", display:"grid", gridTemplateColumns:"1fr 340px", gap:20, opacity:canWrite?1:0.75, pointerEvents:canWrite?"auto":"none" }}>
        {/* Left — questions */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <Card style={{ padding:20, display:"flex", flexDirection:"column", gap:14 }}>
            <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.surveyName", "Survey name")}<span style={{ color:T.red1, marginLeft:3 }}>*</span></div><Input value={form.name} onChange={(e)=>set({name:e.target.value})} placeholder={t("surveys.editorPage.eGPostEventSatisfaction", "e.g. Post-event satisfaction")}/></div>
            <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.description", "Description")}</div><Input value={form.description} onChange={(e)=>set({description:e.target.value})} placeholder={t("surveys.editorPage.whenToUseThisSurvey", "When to use this survey")}/></div>
          </Card>
          <Card style={{ padding:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div>
                <h3 style={{ ...tx.h3, fontWeight:700 }}>{t("surveys.editorPage.questions", "Questions")}</h3>
                <p style={{ ...tx.help, marginTop:2 }}>{t("surveys.editorPage.dragToReorderEachQuestionIsPickedFromTheSharedSurveyQuestions", "Drag to reorder. Each question is picked from the shared survey questions library.")}</p>
              </div>
              <Btn variant="secondary" size="sm" icon={<Icon.plus size={13}/>} onClick={()=>setPicking(true)}>{t("surveys.editorPage.addQuestion", "Add question")}</Btn>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {form.items.map((it,i)=>{ const q=(editorSqList ?? []).find((x)=>x.id===it.qid); if(!q) return null; return (
                <div key={it.qid} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", border:`1px solid ${T.border}`, borderRadius:8, background:T.white }}>
                  <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
                    <IconBtn size={20} label={t("surveys.editorPage.up", "Up")}   onClick={()=>move(i,-1)}><Icon.chev dir="up"   size={12}/></IconBtn>
                    <IconBtn size={20} label={t("surveys.editorPage.down", "Down")} onClick={()=>move(i,1)}>  <Icon.chev dir="down" size={12}/></IconBtn>
                  </div>
                  <span style={{ width:24, height:24, borderRadius:999, background:T.p4, color:T.p1, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, flexShrink:0 }}>{i+1}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13.5, fontWeight:600, color:T.n1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{q.label}</div>
                    <div style={{ fontSize:11.5, color:T.soft }}>{QTYPE(q.type).label}</div>
                  </div>
                  <IconBtn size={30} label={t("common.actions.remove", "Remove")} onClick={()=>removeItem(it.qid)}><Icon.trash size={14}/></IconBtn>
                </div>
              ); })}
              {form.items.length===0 ? (
                <div style={{ padding:28, border:`1.5px dashed ${T.n4}`, borderRadius:10, textAlign:"center" }}>
                  <div style={{ fontSize:14, fontWeight:600, color:T.n1, marginBottom:4 }}>{t("surveys.editorPage.noQuestionsYet", "No questions yet")}</div>
                  <div style={{ fontSize:12.5, color:T.soft, marginBottom:12 }}>{t("surveys.editorPage.pickFromTheSharedSurveyQuestionsLibrary", "Pick from the shared survey questions library.")}</div>
                  <Btn size="sm" icon={<Icon.plus size={13}/>} onClick={()=>setPicking(true)}>{t("surveys.editorPage.addQuestion", "Add question")}</Btn>
                </div>
              ):null}
            </div>
          </Card>
        </div>

        {/* Right — send timing + channel */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <Card style={{ padding:20, display:"flex", flexDirection:"column", gap:14 }}>
            <h3 style={{ ...tx.h3, fontWeight:700, marginBottom:2 }}>{t("surveys.editorPage.sendTiming", "Send timing")}</h3>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.mode", "Mode")}</div>
              <div style={{ display:"flex", gap:8 }}>
                {(["relative","absolute"] as const).map((m)=>(
                  /* ui-raw: send-mode choice strip (flex 1, bordered) — SegmentedControl's pattern (group 8) */
                  <button key={m} onClick={()=>setSend({mode:m})} style={{ flex:1, padding:"8px 0", borderRadius:8, border:`1.5px solid ${form.send.mode===m?T.p1:T.border}`, background:form.send.mode===m?T.p4:T.white, color:form.send.mode===m?T.p1:T.n2, fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:T.font }}>
                    {m==="relative"?t("surveys.surveyEditorPage.relative", "Relative"):t("surveys.surveyEditorPage.absolute", "Absolute")}
                  </button>
                ))}
              </div>
            </div>
            {form.send.mode==="relative"?(
              <>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.amount", "Amount")}</div><input type="number" min={1} value={form.send.amount??1} onChange={(e)=>setSend({amount:Math.max(1,+e.target.value)})} style={{ width:"100%", height:38, padding:"0 12px", border:`1px solid ${T.n4}`, borderRadius:8, fontFamily:T.font, fontSize:13, color:T.n1, boxSizing:"border-box" }}/></div>
                  <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.unit", "Unit")}</div><Dropdown value={form.send.unit} onChange={(v)=>setSend({unit:v as any})} aria-label={t("surveys.editorPage.unit", "Unit")} options={[
                    { value: t("surveys.editorPage.minutes", "Minutes"), label: t("surveys.editorPage.minutes", "Minutes") },
                    { value: t("surveys.editorPage.hours", "Hours"), label: t("surveys.editorPage.hours", "Hours") },
                    { value: t("surveys.editorPage.days", "Days"), label: t("surveys.editorPage.days", "Days") },
                    { value: t("surveys.editorPage.weeks", "Weeks"), label: t("surveys.editorPage.weeks", "Weeks") },
                  ]} /></div>
                </div>
                <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.trigger", "Trigger")}</div><Dropdown value={form.send.trigger} onChange={(v)=>setSend({trigger:v as any})} aria-label={t("surveys.editorPage.trigger", "Trigger")} options={[
                  { value: "after", label: t("surveys.editorPage.afterEventEnds", "After event ends") },
                  { value: "before", label: t("surveys.editorPage.beforeEventStarts", "Before event starts") },
                ]} /></div>
              </>
            ):(
              <div><div style={{ fontSize:12, fontWeight:600, color:T.n2, marginBottom:5 }}>{t("surveys.editorPage.dateTime", "Date & time")}</div><input type="datetime-local" value={form.send.datetime??""} onChange={(e)=>setSend({datetime:e.target.value})} style={{ width:"100%", height:38, padding:"0 12px", border:`1px solid ${T.n4}`, borderRadius:8, fontFamily:T.font, fontSize:13, color:T.n1, boxSizing:"border-box" }}/></div>
            )}
          </Card>
          <Card style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
            <h3 style={{ ...tx.h3, fontWeight:700 }}>{t("surveys.editorPage.deliveryChannel", "Delivery channel")}</h3>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {svChannels().map((ch)=>(
                <label key={ch.id} onClick={()=>setSend({channel:ch.id})} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", padding:"8px 10px", borderRadius:8, border:`1.5px solid ${form.send.channel===ch.id?ch.color:T.border}`, background:form.send.channel===ch.id?alpha(ch.color,"10"):"transparent" }}>
                  <input type="radio" readOnly checked={form.send.channel===ch.id} style={{ accentColor:ch.color }}/>
                  <ChipTag label={ch.name} color={ch.color}/>
                </label>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Question picker modal */}
      {picking?(
        <div onClick={()=>setPicking(false)} style={{ position:"fixed", inset:0, zIndex:250, background:"rgba(20,16,46,0.55)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div onClick={(e)=>e.stopPropagation()} style={{ width:560, maxWidth:"94vw", maxHeight:"84vh", background:T.white, borderRadius:14, boxShadow:`0 20px 50px -12px rgba(16,24,40,.4)`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <h2 style={{ fontSize:16, fontWeight:700, color:T.n1, margin:0 }}>{t("surveys.editorPage.addSurveyQuestions", "Add survey questions")}</h2>
              <IconBtn label={t("common.actions.close", "Close")} onClick={()=>setPicking(false)}><Icon.x size={15}/></IconBtn>
            </div>
            <div style={{ flex:1, overflow:"auto", padding:"8px 12px 12px" }}>
              {(editorSqList ?? []).map((q)=>(
                <MediaRow key={q.id} onClick={()=>{addItem(q);}}
                  title={<>{q.label}</>} subtitle={<>{QTYPE(q.type).label}</>}
                  trailing={<>{form.items.some((it)=>it.qid===q.id)?<Pill tone="success" dot>{t("surveys.editorPage.added", "Added")}</Pill>:<Icon.plus size={16} style={{ marginLeft:"auto", flexShrink:0, color:T.soft }}/>}</>}
                  style={{ borderRadius: 9, padding: "10px 12px" }} />

              ))}
            </div>
            <div style={{ padding:"10px 16px", borderTop:`1px solid ${T.border}`, display:"flex", justifyContent:"flex-end" }}>
              <Btn onClick={()=>setPicking(false)}>{t("common.actions.done", "Done")}</Btn>
            </div>
          </div>
        </div>
      ):null}
    </Screen>
  );
}

export default function SurveysPage() { return <SurveysListTab/>; }
export function SurveyQuestionsPage() { return <SurveyQuestionsTab/>; }
